import { useEffect, useMemo } from "react";
import { EditorState, EditorAction } from "../types"

interface UseAutoCompleteProps {
    state: EditorState
    value: string
    dispatch: React.Dispatch<EditorAction>
    words?: string[]
}

export const useAutoComplete = ({ state, value, dispatch, words }: UseAutoCompleteProps) => {
    // Effect to update autocomplete based on words prop
    useEffect(() => {
        dispatch({ type: 'UPDATE_AUTOCOMPLETE_FROM_WORDS', payload: words })
    }, [dispatch, words])

    // Effect to handle suggestions based on current word
    useEffect(() => {
        const getCurrentWord = (): string => {
            if (value) {
                const beforeCaret = value.slice(0, state.selStart)
                const match = beforeCaret.match(/(\w+)$/)
                return match ? match[1] : ""
            } else {
                return ""
            }
        }

        const currentWord = getCurrentWord()

        if (!currentWord) {
            dispatch({ type: 'SET_SUGGESTIONS', payload: { suggestions: [], show: false } })
            return
        }

        if (!words || !state.autoComplete) {
            return
        }

        const matches = words.filter((w) =>
            w.toLowerCase().startsWith(currentWord.toLowerCase())
        )

        // Check if the current word is already an exact match
        for (let i = 0; i < matches.length; i++) {
            if (matches[i].toLowerCase() === currentWord.toLowerCase()) {
                return
            }
        }

        dispatch({
            type: 'SET_SUGGESTIONS',
            payload: {
                suggestions: matches,
                show: matches.length > 0
            }
        })
    }, [value, state.selStart, words, state.autoComplete, dispatch])

    // Memoized filtered suggestions to avoid repeated filtering
    const filteredSuggestions = useMemo(() => {
        if (!value || !state.autoComplete || !words) return []

        const getCurrentWord = (): string => {
            const beforeCaret = value.slice(0, state.selStart)
            const match = beforeCaret.match(/(\w+)$/)
            return match ? match[1] : ""
        }

        const currentWord = getCurrentWord()
        if (!currentWord) return []

        return words.filter((w) =>
            w.toLowerCase().startsWith(currentWord.toLowerCase())
        )
    }, [value, state.selStart, words, state.autoComplete])

    return {
        filteredSuggestions
    }
}
